<template>
  <section class="empty-listing">
    <ListingBlockHeader v-if="title" :title="title" />
    <div
      class="empty-listing__inner"
      :class="{ 'empty-listing__inner--default': useDefaultImage }"
    >
      <img
        v-if="useDefaultImage"
        class="empty-listing__img empty-listing__img--light"
        src="@/assets.app/images/empty_listing_block.svg"
      />
      <img
        v-if="useDefaultImage"
        class="empty-listing__img empty-listing__img--dark"
        src="@/assets.app/images/empty_listing_block_dark.svg"
      />
      <slot></slot>
    </div>
  </section>
</template>

<script setup lang="ts">
import ListingBlockHeader from '@/app/components/listing/ListingBlockHeader.vue'

/**
 * Empty state for listing blocks.
 *
 * Adds the listing title and a background the wrapped elements.
 *
 * @param title - The title of the listing block.
 * @param useDefaultImage - Whether or not to use the default empty listing image at the top of the block.
 */

withDefaults(
  defineProps<{
    title?: string
    useDefaultImage?: boolean
  }>(),
  {
    useDefaultImage: false,
  },
)
</script>

<style lang="scss" scoped>
.empty-listing {
  &__inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
    padding: 30px;
    border-radius: 12px;
    background: $c-bg;

    :deep(p) {
      margin: 0;
      text-align: center;
      max-width: 700px;

      &:not(:last-child) {
        margin-bottom: 1em;
      }
    }

    :deep(img) {
      border-radius: 4px;
    }
  }

  &__img {
    width: 140px;
  }
  &__img--dark {
    display: none;
  }
}

.empty-listing--default {
  /**
   * The default image has some empty pixels above it.
   * This extra bottom padding compensates for that, 
   * making the content look visually centered.
   */
  .empty-listing__inner {
    padding-bottom: 37px;
  }
}

.night-mode .empty-listing {
  &__inner {
    background: $c-dark-2;

    :deep(p) {
      color: $c-dark-8;
    }
  }

  &__img--light {
    display: none;
  }
  &__img--dark {
    display: block;
  }
}
</style>
